// Batch 0 (UX/IA overhaul) — the shared table.
//
// Replaces every hand-rolled <table> (Cameras, Sites, Users, Tenants,
// Admin·Cameras, Health, Alerts, Detections, Evidence…), each of which today
// re-implements headers, empty/loading states, and — where they bother —
// sorting and pagination differently. One typed, accessible table with:
//   * sticky header, zebra rows, row-click drill-down
//   * controlled sort (server- or client-friendly) with indicators
//   * built-in loading / error / empty states (guiding EmptyState)
//   * an optional footer <Pagination>
// Sorting/paging are CONTROLLED so the same component backs both a client-side
// list and a server list that maps onto the backend's limit/offset.
import * as React from "react";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string;
  dir: SortDir;
}

export interface Column<T> {
  /** Stable key; also the sort key sent to onSortChange when sortable. */
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  /** Extra classes for both the <th> and each <td>. */
  className?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;

  // state flags
  isLoading?: boolean;
  error?: string | null;

  // sorting (controlled)
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;

  // pagination (controlled; omit to hide the footer)
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
  };

  // empty state
  emptyTitle?: string;
  emptyDescription?: React.ReactNode;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  emptyAction?: React.ReactNode;

  /** Row height. "comfortable" (default) or "compact" for dense operator
   *  lists (Cameras/Health/Alerts/Detections) that need more rows per screen. */
  density?: "comfortable" | "compact";
  className?: string;
}

const alignClass = { left: "text-left", right: "text-right", center: "text-center" };
const cellPad = { comfortable: "px-4 py-3", compact: "px-3 py-1.5" };

function nextSort(current: SortState | null | undefined, key: string): SortState {
  if (current?.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: "asc" };
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isLoading,
  error,
  sort,
  onSortChange,
  pagination,
  emptyTitle = "Nothing to show",
  emptyDescription,
  emptyIcon,
  emptyAction,
  density = "comfortable",
  className,
}: DataTableProps<T>) {
  const colCount = columns.length;
  const pad = cellPad[density];

  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  const sortable = c.sortable && !!onSortChange;
                  return (
                    <th
                      key={c.key}
                      className={cn(
                        "font-medium", pad,
                        alignClass[c.align ?? "left"],
                        sortable && "cursor-pointer select-none hover:text-foreground",
                        c.className,
                      )}
                      onClick={
                        sortable
                          ? () => onSortChange!(nextSort(sort, c.key))
                          : undefined
                      }
                      aria-sort={
                        active ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.header}
                        {sortable &&
                          (active ? (
                            sort!.dir === "asc" ? (
                              <ArrowUpIcon className="h-3 w-3" />
                            ) : (
                              <ArrowDownIcon className="h-3 w-3" />
                            )
                          ) : (
                            <ArrowUpDownIcon className="h-3 w-3 opacity-40" />
                          ))}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={colCount} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}

              {!isLoading && error && (
                <tr>
                  <td colSpan={colCount} className="px-4 py-10 text-center text-sm text-destructive">
                    {error}
                  </td>
                </tr>
              )}

              {!isLoading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="p-0">
                    <EmptyState
                      card={false}
                      icon={emptyIcon}
                      title={emptyTitle}
                      description={emptyDescription}
                      action={emptyAction}
                    />
                  </td>
                </tr>
              )}

              {!isLoading &&
                !error &&
                rows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    className={cn(
                      "border-b last:border-0",
                      onRowClick && "cursor-pointer hover:bg-muted/40",
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={cn(pad, alignClass[c.align ?? "left"], c.className)}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {pagination && !isLoading && !error && (
          <div className="border-t">
            <Pagination {...pagination} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
